import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';

type EnvRow = {
  id: number;
  name: string;
  description?: string;
  url?: string;
  baseUrl?: string;
  environmentType?: string;
  projectId?: number;
  tenantId?: number;
  deletedAt?: string | null;
  usageCount?: number;
  usageCountLast7d?: number;
};

type AssignedUserRow = {
  userId: number;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  status?: string;
};

const ROLE_OPTIONS = ['ADMIN', 'MEMBER', 'PROJECT_MANAGER', 'ADMIN_MEMBER'];

const SuperAdminEnvironmentManagementPage: React.FC = () => {
  const { user } = useAuth();
  const createdByDefault = useMemo(() => {
    const u = user as any;
    const first = (u?.firstName || u?.first_name || '').trim();
    const last = (u?.lastName || u?.last_name || '').trim();
    if (first || last) return `${first} ${last}`.trim();
    return (u?.email || '').trim();
  }, [user]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [envs, setEnvs] = useState<EnvRow[]>([]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getAdminEnvironmentManagementEnvironments(includeArchived);
      setEnvs(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load environments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeArchived]);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    projectId: '',
    name: '',
    description: '',
    url: '',
    environmentType: 'DEV',
    createdBy: createdByDefault,
  });

  useEffect(() => {
    setCreateForm((prev) => ({ ...prev, createdBy: createdByDefault }));
  }, [createdByDefault]);

  const handleCreate = async () => {
    setError(null);
    const projectIdNum = Number(createForm.projectId);
    if (!projectIdNum) {
      setError('projectId is required');
      return;
    }
    if (!createForm.name.trim()) {
      setError('Environment name is required');
      return;
    }
    if (!createForm.url.trim()) {
      setError('Environment URL is required');
      return;
    }

    setLoading(true);
    try {
      await apiService.createAdminEnvironment({
        projectId: projectIdNum,
        name: createForm.name.trim(),
        description: createForm.description,
        url: createForm.url.trim(),
        environmentType: createForm.environmentType,
        createdBy: createForm.createdBy,
      });
      setCreateOpen(false);
      setCreateForm((prev) => ({ ...prev, name: '', description: '', url: '', environmentType: 'DEV' }));
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to create environment');
    } finally {
      setLoading(false);
    }
  };

  const archiveEnv = async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      await apiService.archiveAdminEnvironment(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to archive environment');
    } finally {
      setLoading(false);
    }
  };

  const restoreEnv = async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      await apiService.restoreAdminEnvironment(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to restore environment');
    } finally {
      setLoading(false);
    }
  };

  // Assigned users dialog
  const [assignedOpen, setAssignedOpen] = useState(false);
  const [assignedEnv, setAssignedEnv] = useState<EnvRow | null>(null);
  const [assignedLoading, setAssignedLoading] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState<AssignedUserRow[]>([]);
  const [savingRole, setSavingRole] = useState<Record<number, boolean>>({});

  const openAssigned = async (env: EnvRow) => {
    setAssignedEnv(env);
    setAssignedOpen(true);
    setAssignedLoading(true);
    setAssignedUsers([]);
    try {
      const data = await apiService.getAdminEnvironmentAssignedUsers(env.id);
      setAssignedUsers(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load assigned users');
    } finally {
      setAssignedLoading(false);
    }
  };

  const updateRole = async (userId: number, role: string) => {
    if (!assignedEnv) return;
    setSavingRole((prev) => ({ ...prev, [userId]: true }));
    setError(null);
    try {
      await apiService.updateAdminEnvironmentAssignedUserRole(assignedEnv.id, userId, role);
      // refresh
      const data = await apiService.getAdminEnvironmentAssignedUsers(assignedEnv.id);
      setAssignedUsers(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to update role');
    } finally {
      setSavingRole((prev) => ({ ...prev, [userId]: false }));
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 800 }}>
        🌐 Environment Management
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
        <Button variant="contained" onClick={() => setCreateOpen(true)}>
          Create Environment
        </Button>
        <Button
          variant={includeArchived ? 'contained' : 'outlined'}
          onClick={() => setIncludeArchived((v) => !v)}
        >
          {includeArchived ? 'Showing Archived' : 'Include Archived'}
        </Button>
        <Box sx={{ flex: 1 }} />
        {loading && <CircularProgress size={22} />}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Environment</TableCell>
              <TableCell>Project</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Usage</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {envs.map((e) => (
              <TableRow key={e.id}>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {e.name} {e.environmentType ? `(${e.environmentType})` : ''}
                    </Typography>
                    {e.url ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {e.url}
                      </Typography>
                    ) : null}
                  </Box>
                </TableCell>
                <TableCell>{e.projectId ?? '-'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={e.deletedAt ? 'default' : 'success'}
                    label={e.deletedAt ? 'ARCHIVED' : 'ACTIVE'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {e.usageCount ?? 0} total
                    <Typography component="span" variant="caption" color="text.secondary">
                      {' '}|{' '}last 7d: {e.usageCountLast7d ?? 0}
                    </Typography>
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => openAssigned(e)} sx={{ mr: 1 }}>
                    Assign Users
                  </Button>
                  {e.deletedAt ? (
                    <Button size="small" variant="outlined" onClick={() => restoreEnv(e.id)}>
                      Restore
                    </Button>
                  ) : (
                    <Button size="small" variant="outlined" color="error" onClick={() => archiveEnv(e.id)}>
                      Archive
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {envs.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No environments found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Create Environment Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Environment</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <TextField
              label="Project ID"
              size="small"
              value={createForm.projectId}
              onChange={(ev) => setCreateForm((p) => ({ ...p, projectId: ev.target.value }))}
            />
            <TextField
              label="Name"
              size="small"
              value={createForm.name}
              onChange={(ev) => setCreateForm((p) => ({ ...p, name: ev.target.value }))}
            />
            <TextField
              label="Environment Type (DEV/QA/PROD)"
              size="small"
              value={createForm.environmentType}
              onChange={(ev) => setCreateForm((p) => ({ ...p, environmentType: ev.target.value }))}
              helperText="Stored into environmentType for filtering/grouping."
            />
            <TextField
              label="URL"
              size="small"
              value={createForm.url}
              onChange={(ev) => setCreateForm((p) => ({ ...p, url: ev.target.value }))}
            />
            <TextField
              label="Description"
              size="small"
              multiline
              rows={3}
              value={createForm.description}
              onChange={(ev) => setCreateForm((p) => ({ ...p, description: ev.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} variant="text">
            Cancel
          </Button>
          <Button onClick={handleCreate} variant="contained" disabled={loading || !createForm.name.trim() || !createForm.url.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Users Dialog */}
      <Dialog open={assignedOpen} onClose={() => setAssignedOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Assign Users {assignedEnv ? `— ${assignedEnv.name}` : ''}
        </DialogTitle>
        <DialogContent>
          {assignedLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={26} />
            </Box>
          ) : (
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Save</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignedUsers.map((u) => (
                    <TableRow key={u.userId}>
                      <TableCell>{u.email}</TableCell>
                      <TableCell sx={{ minWidth: 260 }}>
                        <Select
                          size="small"
                          value={u.role || 'MEMBER'}
                          onChange={(e) =>
                            setAssignedUsers((prev) =>
                              prev.map((x) => (x.userId === u.userId ? { ...x, role: e.target.value as string } : x))
                            )
                          }
                        >
                          {ROLE_OPTIONS.map((r) => (
                            <MenuItem key={r} value={r}>
                              {r}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>{u.status || '-'}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!!savingRole[u.userId]}
                          onClick={() => updateRole(u.userId, u.role || 'MEMBER')}
                        >
                          {savingRole[u.userId] ? 'Saving...' : 'Update'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {assignedUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No users found for this environment/project tenant.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignedOpen(false)} variant="text">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuperAdminEnvironmentManagementPage;

